import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { history } from "../../redux/store/configureStore";
import * as clientLedgerActions from "../../redux/actions/clientLedgerActions";
import * as clientActions from "../../redux/actions/clientActions";
import { getById } from "../common/Helpers";
import ClientLedgerForm from "./ClientLedgerForm";
import ClientLedgerList from "./ClientLedgerList";
import { CLIENT_LEDGER_ENTRY_TYPES, PAYMENT_METHODS } from "./clientLedgerTypes";
import { normalizeClientLedgerEntry, validateClientLedgerEntry } from "./clientLedgerValidation";

const formatDatePart = value => String(value).padStart(2, "0");

const currentDate = () => {
  const date = new Date();
  return `${date.getFullYear()}-${formatDatePart(date.getMonth() + 1)}-${formatDatePart(date.getDate())}`;
};

const currentMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${formatDatePart(date.getMonth() + 1)}`;
};

const createEmptyEntry = clientId => ({
  clientId,
  type: CLIENT_LEDGER_ENTRY_TYPES.PAYMENT,
  date: currentDate(),
  settlementMonth: currentMonth(),
  amount: "",
  method: PAYMENT_METHODS.TRANSFER,
  note: "",
});

const resetEntry = clientId => createEmptyEntry(clientId);

export function ClientLedgerPage({
  clients,
  client,
  clientLedger,
  loadClients,
  loadClientLedger,
  saveClientLedgerEntry,
  deleteClientLedgerEntry,
}) {
  const [entry, setEntry] = useState(createEmptyEntry(client.id));
  const [errors, setErrors] = useState({});
  const [editingEntryId, setEditingEntryId] = useState("");
  const entries = clientLedger
    .filter(item => item.clientId === client.id)
    .sort((a, b) => (a.entryKey || "").localeCompare(b.entryKey || ""));

  useEffect(() => {
    if (clients.length === 0) {
      loadClients().catch(error => alert(`Loading clients failed ${error}`));
    }
  }, [clients.length, loadClients]);

  useEffect(() => {
    if (client.id) {
      loadClientLedger(client.id).catch(error => alert(`Loading clientLedger failed ${error}`));
    }
  }, [client.id, loadClientLedger]);

  useEffect(() => {
    if (client.id) {
      setEntry(resetEntry(client.id));
      setErrors({});
      setEditingEntryId("");
    }
  }, [client.id]);

  const handleChange = event => {
    const { name, value } = event.target;
    setEntry(prevEntry => ({ ...prevEntry, [name]: value }));
  };

  const handleEdit = selectedEntry => {
    setEntry({
      ...selectedEntry,
      amount: String(selectedEntry.amount),
    });
    setErrors({});
    setEditingEntryId(selectedEntry.id);
  };

  const handleCancelEdit = () => {
    setEntry(resetEntry(client.id));
    setErrors({});
    setEditingEntryId("");
  };

  const handleDelete = selectedEntry => {
    if (!window.confirm("Usunac ten wpis?")) return;

    deleteClientLedgerEntry(selectedEntry)
      .then(() => {
        if (editingEntryId === selectedEntry.id) {
          handleCancelEdit();
        }
      })
      .catch(error => setErrors({ saveError: error.message }));
  };

  const handleSubmit = event => {
    event.preventDefault();
    const validationErrors = validateClientLedgerEntry(entry);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const normalizedEntry = normalizeClientLedgerEntry(entry, Date.now().toString());

    saveClientLedgerEntry(normalizedEntry, !editingEntryId)
      .then(() => {
        setEntry(resetEntry(client.id));
        setErrors({});
        setEditingEntryId("");
      })
      .catch(error => setErrors({ saveError: error.message }));
  };

  if (!client.id) {
    return <div className="manage-text">Ladowanie klienta...</div>;
  }

  return (
    <div className="mb-5">
      <h2 className="manage-title">{`Rozliczenia: ${client.firstName || ""} ${client.lastName || ""}`}</h2>
      {errors.saveError && <div role="alert" className="alert alert-danger">{errors.saveError}</div>}
      <ClientLedgerForm
        entry={entry}
        errors={errors}
        onChange={handleChange}
        onSubmit={handleSubmit}
        isEditing={!!editingEntryId}
        onCancel={handleCancelEdit}
      />
      <div className="mt-4">
        <ClientLedgerList entries={entries} onEdit={handleEdit} onDelete={handleDelete} />
      </div>
      <button type="button" className="btn manage-button manage-teal-button mt-3" onClick={() => history.push(`/manage/client/${client.id}`)}>Powrot</button>
    </div>
  );
}

ClientLedgerPage.propTypes = {
  clients: PropTypes.array.isRequired,
  client: PropTypes.object.isRequired,
  clientLedger: PropTypes.array.isRequired,
  loadClients: PropTypes.func.isRequired,
  loadClientLedger: PropTypes.func.isRequired,
  saveClientLedgerEntry: PropTypes.func.isRequired,
  deleteClientLedgerEntry: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const { id } = ownProps.match.params;
  return {
    clients: state.clients,
    client: getById(state.clients, id) || {},
    clientLedger: state.clientLedger,
  };
};

export default connect(mapStateToProps, {
  loadClients: clientActions.loadClients,
  loadClientLedger: clientLedgerActions.loadClientLedger,
  saveClientLedgerEntry: clientLedgerActions.saveClientLedgerEntry,
  deleteClientLedgerEntry: clientLedgerActions.deleteClientLedgerEntry,
})(ClientLedgerPage);
